import React, { useEffect, useState } from 'react'
import { useAuthStore } from '../../store/authStore'
import { GovDashboard } from '../../modules/government/ui/GovDashboard'
import { getUserType } from '../../utils/userPermissions'
import { Navigate } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'

export const GovDashboardPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const [isLoading, setIsLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    checkAccess()
  }, [user, isAuthenticated])

  const checkAccess = async () => {
    if (!isAuthenticated || !user) {
      console.log('❌ GovDashboardPage - No authentication or user')
      setIsLoading(false)
      return
    }

    console.log('🔍 GovDashboardPage - User data:', {
      email: user.email,
      role: user.role,
      isAgency: user.isAgency,
      agencyId: user.agencyId,
      isGovCertified: user.isGovCertified,
      isEnterprise: user.isEnterprise,
      verified: user.verified,
      isEmailVerified: user.isEmailVerified
    })

    const userType = getUserType(user)
    console.log('🔍 GovDashboardPage - Detected userType:', userType)

    // Allow ADMIN, AGENCY, ENTERPRISE_VENDOR, and government email domains
    const isGovUser = userType === 'ADMIN' || 
                     userType === 'ENTERPRISE_VENDOR' || 
                     userType === 'AGENCY' ||
                     user.email?.includes('@federal.gov') ||
                     user.email?.includes('@gov') ||
                     user.email?.includes('@energy.dept')
    
    console.log('🔍 GovDashboardPage - isGovUser:', isGovUser, {
      userType,
      isAdmin: userType === 'ADMIN',
      isAgency: userType === 'AGENCY',
      isEnterpriseVendor: userType === 'ENTERPRISE_VENDOR',
      isGovEmail: user.email?.includes('@federal.gov') || user.email?.includes('@gov') || user.email?.includes('@energy.dept')
    })

    if (!isGovUser) {
      console.log('❌ GovDashboardPage - Access denied - not a government user')
      setIsLoading(false)
      return
    }

    console.log('✅ GovDashboardPage - Access granted')
    setHasAccess(true)
    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Verifying government access...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Access Denied
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have access to the Government Contracting system.
            </p>
            <button
              onClick={() => window.history.back()}
              className="btn btn-primary"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Determine the appropriate role for the dashboard
  let userRole: 'ENTERPRISE_VENDOR' | 'AGENCY' | 'ADMIN' | 'GOV_EMAIL'
  
  const userType = getUserType(user)
  
  // If user has government email domain, treat them as GOV_EMAIL regardless of their actual role
  if (user && (user.email?.includes('@federal.gov') || 
      user.email?.includes('@gov') || 
      user.email?.includes('@energy.dept'))) {
    userRole = 'GOV_EMAIL'
  } else {
    // Use their actual user type
    userRole = userType as 'ENTERPRISE_VENDOR' | 'AGENCY' | 'ADMIN'
  }

  return (
    <div className="gov-dashboard-page">
      <GovDashboard userRole={userRole} />
    </div>
  )
}